import { ReactElement, useCallback, useContext, useEffect } from "react";

import { Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import { FormType, PaymentMethod } from "@simplyk/common";
import { ExpressCheckoutElement as StripeExpressCheckoutElement } from "@stripe/react-stripe-js";
import { enqueueSnackbar } from "notistack";
import { FieldValues } from "react-hook-form";

import { AmplitudeEvents } from "../../constants/amplitude";
import { FrontendTicketingContext } from "../../contexts/FrontendTicketingContext";
import { useIsFormV2 } from "../../features/FormV2/hooks/useIsFormV2";
import { usePreviewContext } from "../../features/LiveFormEditor/LivePreview/context/PreviewContext";
import { useAmplitude } from "../../hooks/amplitude/useAmplitude";
import { useTranslate } from "../../hooks/useTranslate";
import { DisplayGrid } from "../Display/DisplayGrid";
import { DisplayWithInfoFormPreview } from "../InfoForm/InfoForm";
import PaymentErrorAlert from "../PaymentErrorAlert/PaymentErrorAlert";
import { PaymentMethodOptions } from "../PaymentMethodOptions/PaymentMethodOptions";
import { PreviewPaymentMethods } from "../PaymentMethodOptions/PreviewPaymentMethods";
import { PaymentElementProps } from "../PaymentProcessor/type";
import { SubmitPaymentButton } from "../SubmitPaymentButton/SubmitPaymentButton";
import { UserAgreement } from "../UserAgreement/UserAgreement";

import { usePaymentErrorContext } from "./PaymentErrorContext";
import { useExpressCheckout } from "./useExpressCheckout";
import { usePaymentSubmit } from "./usePaymentSubmit";
import { useSplitCoverFees } from "./useSplitCoverFees";

import { PayAnotherWayDivider } from "@/components/PaymentElement/PayAnotherWayDivider";
import { ThemedHelpZeffyCoverFeesInfobox } from "@/features/FormV2/sharedComponents/ThemedHelpZeffyCoverFeesInfobox";
import { ThemedYoureHelpingZeffyCoverFeesInfobox } from "@/features/FormV2/sharedComponents/ThemedYoureHelpingZeffyCoverFeesInfobox";
import { ThemedZeffySplitOrCoverFeesInfobox } from "@/features/FormV2/sharedComponents/ThemedZeffySplitOrCoverFeesInfobox";
import { StripeExpressCheckoutApplePayPreview } from "@/forms-design-system/FormExpressCheckoutPaymentMethod/StripeExpressCheckoutApplePayPreview";
import { StripeExpressCheckoutGooglePayPreview } from "@/forms-design-system/FormExpressCheckoutPaymentMethod/StripeExpressCheckoutGooglePayPreview";
import { FrontendFormContext } from "@/src/contexts/FrontendFormContext";

export function PaymentElement<T extends FieldValues>({
  handleSubmit,
  formObject,
  validDiscount,
  setValue,
  trigger,
  errors,
  control,
  billingDetails,
  shouldDisplaySubmitButton = true,
}: PaymentElementProps<T>): ReactElement<PaymentElementProps<T>> {
  const isFormV2 = useIsFormV2();
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const { paymentError, setPaymentError } = usePaymentErrorContext();
  const { handleFormSubmit } = usePaymentSubmit({
    handleSubmit,
    formObject,
    validDiscount,
    setValue,
    trigger,
    errors,
    control,
  });
  const theme = useTheme();
  const isDarkMode = theme.palette.metadata?.mode === "dark";
  const { ticketingField } = useContext(FrontendTicketingContext);

  const {
    organization,
    formType,
    tip,
    isOnboarding,
    prioritizeBankPayment,
    selectedPaymentMethodIsBank,
    selectedPaymentMethodIsCard,
    formattedFees,
    displayExpressCheckout,
    isExpressCheckoutLoading,
    expressCheckoutDisabled,
    allowedPaymentMethods,
    isPreviewTemplateMode,
    isPaymentDisabled,
  } = useContext(FrontendFormContext);

  const { isPreview: isLiveFormEditorPreview } = usePreviewContext();

  useEffect(() => {
    if (prioritizeBankPayment) {
      logAmplitudeEvent(AmplitudeEvents.FormInsertAmountZeffyModelMessage, {
        abTestMessage: "positive",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prioritizeBankPayment]);

  const {
    expressCheckoutKey,
    showExpressCheckoutLoader,
    handleExpressCheckoutReady,
    handleExpressCheckoutClick,
    handleExpressCheckoutConfirm,
    handleExpressCheckoutLoadError,
  } = useExpressCheckout({ handleFormSubmit, setValue, trigger, control, errors });

  const { handleCoverFees, handleSplitFees, onSplitCoverFeesClose, showSplitCoverFeesInfobox } = useSplitCoverFees();

  const showExpressCheckoutOnboardingSnackbar = useCallback(() => {
    enqueueSnackbar(t("common", "payment.expressCheckout.onboarding"), { vibe: "danger" });
  }, [t]);

  useEffect(() => {
    if (isPaymentDisabled) {
      setPaymentError(
        t("common", "paymentNotReadyBanner.description.disabled", {
          orgEmail: organization?.ownerEmail || "contact@zeffy.com",
        })
      );
    }
  }, [isPaymentDisabled, organization?.ownerEmail, paymentError, setPaymentError, t]);

  return (
    <Grid container>
      <DisplayGrid when={Boolean(paymentError)} item xs={12} marginBottom={isFormV2 ? 0 : 2}>
        <PaymentErrorAlert paymentError={paymentError} />
      </DisplayGrid>
      <DisplayWithInfoFormPreview
        content={t(formType === FormType.DonationForm ? "donationForm" : "ticketing", "preview.paymentMethod")}
      >
        {!expressCheckoutDisabled && !isPreviewTemplateMode && (
          <DisplayGrid when={displayExpressCheckout} item container xs={12} marginBottom={3}>
            <Grid item xs={12} marginBottom={showExpressCheckoutLoader ? 0 : 3}>
              <StripeExpressCheckoutElement
                // To avoid any issues with express checkout not loading we use the key prop
                // to force the express checkout component to re-initialize when it's shown/hidden
                key={expressCheckoutKey}
                id="express-checkout-element"
                // We remove the onReady callback to avoid triggering this callback when hiding the component
                onReady={handleExpressCheckoutReady}
                onLoadError={handleExpressCheckoutLoadError}
                onConfirm={handleExpressCheckoutConfirm}
                onClick={isOnboarding ? showExpressCheckoutOnboardingSnackbar : handleExpressCheckoutClick}
                options={{
                  buttonTheme: {
                    applePay: isDarkMode ? "white" : "black",
                    googlePay: isDarkMode ? "white" : "black",
                  },
                }}
              />
              {showExpressCheckoutLoader && <Skeleton variant="rectangular" height={44} />}
            </Grid>

            {!showExpressCheckoutLoader && <PayAnotherWayDivider />}
          </DisplayGrid>
        )}

        {/* Fake Express Checkout Buttons for Preview */}
        {(isPreviewTemplateMode || isLiveFormEditorPreview) && (
          <>
            <Stack width="100%" gap={0.5} marginBlockEnd={3}>
              <StripeExpressCheckoutApplePayPreview />
              <StripeExpressCheckoutGooglePayPreview />
            </Stack>
            <PayAnotherWayDivider sx={{ marginBlockEnd: 3 }} />
          </>
        )}

        {(!isExpressCheckoutLoading || isPreviewTemplateMode) && !isLiveFormEditorPreview && (
          <Grid item xs={12}>
            <PaymentMethodOptions offlinePaymentWording={ticketingField?.offlinePaymentWording} />
          </Grid>
        )}

        {/* Live Form Editor Preview (Fake payment methods) */}
        {isLiveFormEditorPreview && (
          <Grid item xs={12} marginBottom={6}>
            <PreviewPaymentMethods country={organization.country} />
          </Grid>
        )}
      </DisplayWithInfoFormPreview>
      {billingDetails && (!isExpressCheckoutLoading || isPreviewTemplateMode) && (
        <Grid item xs={12} marginTop={4}>
          {billingDetails}
        </Grid>
      )}

      {prioritizeBankPayment && selectedPaymentMethodIsBank && (
        <Grid item xs={12} marginTop={4}>
          <ThemedYoureHelpingZeffyCoverFeesInfobox organizationName={organization.name} />
        </Grid>
      )}

      {prioritizeBankPayment &&
        selectedPaymentMethodIsCard &&
        tip !== 0 &&
        (allowedPaymentMethods.includes(PaymentMethod.Ach) || allowedPaymentMethods.includes(PaymentMethod.Pad)) && (
          <Grid item xs={12} marginTop={4}>
            <ThemedHelpZeffyCoverFeesInfobox organizationName={organization.name} />
          </Grid>
        )}

      {showSplitCoverFeesInfobox && (
        <Grid item xs={12} marginTop={4}>
          <ThemedZeffySplitOrCoverFeesInfobox
            organizationName={organization.name}
            formattedFees={formattedFees!}
            onSplitFeesClick={handleSplitFees}
            onCoverFeesClick={handleCoverFees}
            onDismiss={onSplitCoverFeesClose}
          />
        </Grid>
      )}

      {shouldDisplaySubmitButton && (
        <Grid container item xs={12} marginTop={4} marginBottom={2}>
          <SubmitPaymentButton handleFormSubmit={handleFormSubmit} />
          <Grid item xs={12} marginTop={1}>
            <UserAgreement />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
